/**
 * Platform Analytics Capabilities
 *
 * Central config defining which metrics each platform provides.
 * Used by AnalyticsDetailDrawer and SnapshotChart to conditionally
 * show/hide metrics and provide context to users.
 *
 * Based on actual backend service returns — see each platform's
 * fetchPostAnalytics / fetchVideoAnalytics in backend/src/services/
 */

export interface PlatformMetricSupport {
  reach: boolean;
  impressions: boolean;
  engagement: boolean;
  likes: boolean;
  comments: boolean;
  shares: boolean;
  saves: boolean;
  clicks: boolean;
  videoViews: boolean;
  videoWatchTime: boolean;
  videoCompletionRate: boolean;
}

export interface PlatformAnalyticsConfig {
  /** Which metrics this platform provides */
  metrics: PlatformMetricSupport;
  /** i18n key for a short note about this platform's analytics (shown in drawer) */
  noteKey?: string;
  /** Default note text (used as i18n fallback when no translation file exists) */
  noteDefault?: string;
}

export const PLATFORM_ANALYTICS: Record<string, PlatformAnalyticsConfig> = {
  LINKEDIN: {
    metrics: {
      reach: true,
      impressions: true,
      engagement: true,
      likes: true,
      comments: true,
      shares: true,
      saves: false,       // Not available in LinkedIn API
      clicks: true,
      videoViews: true,   // Via separate video analytics endpoint
      videoWatchTime: true,
      videoCompletionRate: false,
    },
    noteKey: 'platform_note_linkedin',
    noteDefault: 'Saves are not tracked by LinkedIn. Video metrics are available for video posts.',
  },
  FACEBOOK: {
    metrics: {
      reach: true,
      impressions: true,
      engagement: true,
      likes: true,
      comments: true,
      shares: true,
      saves: false,
      clicks: true,
      videoViews: true,
      videoWatchTime: true,
      videoCompletionRate: true,
    },
  },
  INSTAGRAM: {
    metrics: {
      reach: true,
      impressions: true,
      engagement: true,
      likes: true,
      comments: true,
      shares: true,
      saves: true,
      clicks: true,
      videoViews: true,   // Reels
      videoWatchTime: true,
      videoCompletionRate: true,
    },
  },
  YOUTUBE: {
    metrics: {
      reach: true,        // viewCount — generic label that works for all content
      impressions: false,  // Same as reach (viewCount) — avoid duplicate
      engagement: true,
      likes: true,
      comments: true,
      shares: true,
      saves: true,        // favorites
      clicks: false,
      videoViews: false,  // Same as reach (viewCount) — avoid duplicate
      videoWatchTime: true,
      videoCompletionRate: true,
    },
    noteKey: 'platform_note_youtube',
    noteDefault: 'YouTube uses view count as reach. Watch time and completion rate are available for videos.',
  },
  TIKTOK: {
    metrics: {
      reach: true,        // view_count — works for both video and image posts
      impressions: false,  // Same as reach (view_count) — avoid duplicate
      engagement: true,
      likes: true,
      comments: true,
      shares: true,       // share_count returned by TikTok API, mapped to shares field
      saves: false,
      clicks: false,
      videoViews: false,  // Same as reach (view_count) — avoid duplicate; "Video Views" misleading for image posts
      videoWatchTime: false,
      videoCompletionRate: false,
    },
    noteKey: 'platform_note_tiktok',
    noteDefault: 'TikTok uses view count as reach. Impressions and video views are the same metric.',
  },
  PINTEREST: {
    metrics: {
      reach: false,
      impressions: true,
      engagement: true,
      likes: false,       // Not in Pinterest API
      comments: false,
      shares: true,       // outbound clicks
      saves: true,        // pin saves
      clicks: true,       // outbound clicks
      videoViews: false,
      videoWatchTime: false,
      videoCompletionRate: false,
    },
    noteKey: 'platform_note_pinterest',
    noteDefault: 'Pinterest tracks impressions, outbound clicks, and pin saves. Likes and comments are not available.',
  },
  THREADS: {
    metrics: {
      reach: true,        // views — single distribution metric
      impressions: false,  // Same as reach (views) — avoid duplicate
      engagement: true,
      likes: true,
      comments: true,
      shares: true,       // reposts
      saves: false,
      clicks: false,
      videoViews: false,
      videoWatchTime: false,
      videoCompletionRate: false,
    },
    noteKey: 'platform_note_threads',
    noteDefault: 'Threads uses view count as reach. Impressions are the same metric.',
  },
  GOOGLE_MY_BUSINESS: {
    metrics: {
      reach: true,        // search impressions — single distribution metric
      impressions: false,  // Same as reach (search impressions) — avoid duplicate
      engagement: true,
      likes: false,
      comments: false,
      shares: false,
      saves: false,
      clicks: true,       // actions (website, directions, calls)
      videoViews: false,
      videoWatchTime: false,
      videoCompletionRate: false,
    },
    noteKey: 'platform_note_google',
    noteDefault: 'Google Business uses search impressions as reach, plus actions (website clicks, calls, directions).',
  },
  BLUESKY: {
    metrics: {
      reach: false,       // Decentralized — no view tracking
      impressions: false,
      engagement: true,
      likes: true,
      comments: true,     // replies
      shares: true,       // reposts
      saves: true,        // bookmarks
      clicks: false,
      videoViews: false,
      videoWatchTime: false,
      videoCompletionRate: false,
    },
    noteKey: 'platform_note_bluesky',
    noteDefault: 'Bluesky is decentralized and does not track reach, impressions, or clicks. Engagement metrics (likes, replies, reposts, bookmarks) are available.',
  },
  TWITTER: {
    metrics: {
      reach: false,
      impressions: false,
      engagement: false,
      likes: false,
      comments: false,
      shares: false,
      saves: false,
      clicks: false,
      videoViews: false,
      videoWatchTime: false,
      videoCompletionRate: false,
    },
    noteKey: 'platform_note_twitter',
    noteDefault: 'X (Twitter) analytics are not supported.',
  },
};

/**
 * Check if a specific metric is supported by a platform
 */
export function isMetricSupported(platform: string, metric: keyof PlatformMetricSupport): boolean {
  const config = PLATFORM_ANALYTICS[platform];
  if (!config) return true; // Unknown platform — show everything
  return config.metrics[metric];
}

/**
 * Get the analytics config for a platform (with fallback showing all metrics)
 */
export function getPlatformAnalytics(platform: string): PlatformAnalyticsConfig {
  return PLATFORM_ANALYTICS[platform] ?? {
    metrics: {
      reach: true,
      impressions: true,
      engagement: true,
      likes: true,
      comments: true,
      shares: true,
      saves: true,
      clicks: true,
      videoViews: true,
      videoWatchTime: true,
      videoCompletionRate: true,
    },
  };
}

/**
 * Check if any video metric is supported by a platform
 */
export function supportsVideoMetrics(platform: string): boolean {
  const config = PLATFORM_ANALYTICS[platform];
  if (!config) return true;
  return config.metrics.videoViews || config.metrics.videoWatchTime || config.metrics.videoCompletionRate;
}

/**
 * Check if any distribution metric (reach/impressions/clicks) is supported
 */
export function supportsDistributionMetrics(platform: string): boolean {
  const config = PLATFORM_ANALYTICS[platform];
  if (!config) return true;
  return config.metrics.reach || config.metrics.impressions || config.metrics.clicks;
}
